/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";

import { FOLDERS } from "../../../doseeData/utils/Utils";
import { useAdminContext } from "../../../../context/admin/context/AdminContext";
import { getOneCompany } from "../../../../middlewares/sysNavigation/SysNavigation";
import { ImageKitUpload } from "../../../../middlewares/libraries/imageKit/ImageKitLibrary";
import { useCountryContext } from "../../../../context/country/countryContext/CountryContext";
import {
  Title,
  TextArea,
  MyMessage,
  InputField,
  PhoneInput,
  GoBackTool,
  MyActionButton,
  SearchSelectInput,
  PasswordField,
} from "../../component/Component";

const Edit = () => {
  const { updateBySuperAdmin } = useAdminContext();
  const { countries, fetchCountries } = useCountryContext();

  const company = getOneCompany();
  const company_id = company?.company_id;

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setErrorMessage] = useState(null);

  const admin = company?.Admins?.length > 0 ? company?.Admins[0] : null;

  const [firstName, setFirstName] = useState(admin?.first_name);
  const [lastName, setLastName] = useState(admin?.last_name);
  const [password, setPassword] = useState(null);
  const [email, setEmail] = useState(admin?.email);
  const [phoneNumber, setPhoneNumber] = useState(admin?.phonenumber);

  const [name, setName] = useState(company?.name);
  const [country, setCountry] = useState(company?.country);
  const [website, setWebsite] = useState(company?.website);
  const [thumbnail, setThumbnail] = useState(company?.thumbnail);
  const [thumbnailId, setThumbnailId] = useState(company?.image_cdn_id);
  const [description, setDescription] = useState(company?.description);

  const companyRoutes = "/company";

  const responses = (isError, success, message) => {
    setLoading(false);
    setError(isError);
    setSuccess(success);
    setErrorMessage(message);
  };

  useEffect(() => {
    if (!countries) fetchCountries();
  }, [countries, country]);

  const isValid = ({ data, value, key }) => {
    return data?.find((x) => (x?.[key] === value ? true : false));
  };

  const addOneCompanyHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      responses(true, false, "Image Required");
    } else if (!isValid({ data: countries, value: country, key: "name" })) {
      responses(true, false, "Invalid Country");
    } else await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const data = {
      admin_id: admin?.admin_id,
      name: name,
      thumbnail: thumbnail,
      image_cdn_id: thumbnailId,
      description: description,
      website: website,
      email: email,
      first_name: firstName,
      last_name: lastName,
      password: password && password !== "" ? password : null,
      phonenumber: phoneNumber,
      country: country,
    };

    const { message, status } = await updateBySuperAdmin(company_id, data);

    if (status === 200 || status === 201) {
      setLoading(false);
      setSuccess(true);
      responses(false, true, message);
    } else {
      responses(true, false, message);
    }
  };

  const hasLogo = !!thumbnailId;

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-4 rounded-b-xl">
      <div className="mx-auto w-full max-w-6xl">
        <GoBackTool route={companyRoutes} />

        <div className="relative mt-3 mb-5">
          <div className="pointer-events-none absolute -inset-x-6 -top-6 h-24 rounded-3xl bg-gradient-to-r from-emerald-400/15 via-amber-400/15 to-fuchsia-400/15 blur-2xl -z-10" />
          <div className="relative rounded-2xl border border-gray-200 bg-white/90 shadow-sm backdrop-blur">
            <div className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-gray-900 text-white text-xs font-bold shadow-sm">
                  {name?.slice(0, 2)?.toUpperCase() || "CO"}
                </div>
                <div className="min-w-0">
                  <h1 className="text-base font-semibold tracking-tight text-gray-900">
                    Edit {name}
                  </h1>
                  <p className="mt-0.5 text-xs text-gray-600">
                    Update brand assets, company details and admin information.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <StatusChip label="Logo" ok={hasLogo} />
                <StatusChip label="Country" ok={!!country} />
                <StatusChip label="Admin Email" ok={!!email} />
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={addOneCompanyHandler} className="space-y-5">
          <MyMessage
            error={error}
            message={message}
            success={success}
            onExpired={() => {
              setError(false);
              setSuccess(false);
            }}
            onClick={() => {
              setError(false);
              setSuccess(false);
              setErrorMessage(null);
            }}
          />

          <section className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4 md:p-5">
            <div className="mb-3 flex items-center justify-between">
              <Title props={{ title: "Brand Assets" }} />
              <span className="text-[11px] text-gray-500">
                JPEG/PNG • Square preferred
              </span>
            </div>
            <div className="rounded-xl border border-dashed border-gray-200 p-3">
              <ImageKitUpload
                thumbnail={thumbnail}
                thumbnailId={thumbnailId}
                setThumbnail={setThumbnail}
                setThumbnailId={setThumbnailId}
                folder={FOLDERS.COMPANY}
              />
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4 md:p-5">
            <div className="mb-3">
              <Title props={{ title: "Company Details" }} />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <InputField
                props={{
                  id: "Name",
                  title: "Company Name",
                  type: "text",
                  required: true,
                  value: name ? name : "",
                  onChange: (e) => {
                    setName(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
              <InputField
                props={{
                  id: "Website",
                  title: "Company Website",
                  type: "text",
                  required: false,
                  value: website ? website : "",
                  onChange: (e) => {
                    setWebsite(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
              <SearchSelectInput
                title="Country"
                value={country ? country : null}
                required
                options={countries ? countries?.map((x) => x?.name) : []}
                onChange={(val) => setCountry(val)}
              />
            </div>
            <div className="mt-4">
              <TextArea
                props={{
                  id: "description",
                  title: "Description",
                  type: "text",
                  required: true,
                  value: description ? description : "",
                  onChange: (e) => {
                    setDescription(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4 md:p-5">
            <div className="mb-3">
              <Title props={{ title: "Admin Info" }} />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <InputField
                props={{
                  id: "FirstName",
                  title: "First Name",
                  type: "text",
                  required: true,
                  value: firstName ? firstName : "",
                  onChange: (e) => {
                    setFirstName(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
              <InputField
                props={{
                  id: "LastName",
                  title: "Last Name",
                  type: "text",
                  required: true,
                  value: lastName ? lastName : "",
                  onChange: (e) => {
                    setLastName(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
              <InputField
                props={{
                  id: "Email",
                  title: "Email Address",
                  type: "text",
                  value: email ? email : "",
                  required: true,
                  onChange: (e) => {
                    setEmail(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
              <PhoneInput
                setPhone={setPhoneNumber}
                phone={phoneNumber}
                required={true}
                country={country}
              />
              <PasswordField
                value={password || ""}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(null);
                  setSuccess(null);
                }}
                disabled={loading}
                required={false}
              />
            </div>
          </section>

          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-600">
              <span className="font-semibold">Notice:</span> You can add more
              admins from the company page after saving.
            </p>
            <div className="rounded-xl bg-white shadow-sm ring-1 ring-gray-200 p-2">
              <MyActionButton
                loading={loading}
                title="Save Changes"
                onClick={() => {
                  setError(false);
                  setSuccess(false);
                  setErrorMessage(null);
                }}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Edit;

const StatusChip = ({ label, ok }) => {
  return (
    <span
      className={[
        "inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1",
        ok
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
          : "bg-gray-50 text-gray-700 ring-gray-200",
      ].join(" ")}
    >
      <span
        className={[
          "h-1.5 w-1.5 rounded-full",
          ok ? "bg-emerald-500" : "bg-gray-300",
        ].join(" ")}
      />
      {label}
    </span>
  );
};
